import React, { useState, useEffect } from 'react';
import { useSubdomain } from '../contexts/SiteContext';
import moduleService from '../services/moduleService';
import './ModuleManagement.css';

const ModuleManagement = () => {
  const { sites, loadSites } = useSubdomain();
  const [siteModules, setSiteModules] = useState({});
  const [sitePlans, setSitePlans] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);

  const moduleDefinitions = moduleService.getModuleDefinitions();
  const planDefinitions = moduleService.getPlanDefinitions();

  // Cargar datos cuando cambien los sites
  useEffect(() => {
    if (sites.length > 0 && !selectedSite) {
      setSelectedSite(sites[0].id);
    }
  }, [sites, selectedSite]);

  // Cargar módulos y planes cuando cambie el site seleccionado
  useEffect(() => {
    if (selectedSite) {
      loadSiteData(selectedSite);
    }
  }, [selectedSite]);

  const loadSiteData = async (siteId) => {
    setLoading(true);
    setError(null);

    try {
      // Cargar plan del site
      const planResponse = await moduleService.getSitePlan(siteId);
      setSitePlans(prev => ({
        ...prev,
        [siteId]: planResponse.plan || 'BASIC'
      }));

      // Cargar módulos del site
      const modulesResponse = await moduleService.getSiteModules(siteId);
      setSiteModules(prev => ({
        ...prev,
        [siteId]: modulesResponse.modules || {}
      }));
    } catch (error) {
      setError(`Error cargando datos del site: ${error.message}`);
      console.error('Error loading site data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleModuleToggle = async (siteId, moduleId, enabled) => {
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      await moduleService.toggleSiteModule(siteId, moduleId, enabled);
      
      // Actualizar estado local
      setSiteModules(prev => ({
        ...prev,
        [siteId]: {
          ...prev[siteId],
          [moduleId]: enabled
        }
      }));

      const siteName = sites.find(s => s.id === siteId)?.name;
      const moduleName = moduleDefinitions[moduleId]?.name || moduleId;
      setSuccessMessage(`Módulo "${moduleName}" ${enabled ? 'habilitado' : 'deshabilitado'} para ${siteName}`);
    } catch (error) {
      setError(`Error actualizando módulo: ${error.message}`);
      console.error('Error updating module:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpdate = async (siteId, updates) => {
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      await moduleService.updateSiteModules(siteId, updates);
      
      // Actualizar estado local
      setSiteModules(prev => ({
        ...prev,
        [siteId]: {
          ...prev[siteId],
          ...updates
        }
      }));

      const siteName = sites.find(s => s.id === siteId)?.name;
      setSuccessMessage(`Módulos actualizados exitosamente para ${siteName}`);
    } catch (error) {
      setError(`Error actualizando módulos: ${error.message}`);
      console.error('Error updating modules:', error);
    } finally {
      setLoading(false);
    }
  };

  const isModuleAvailableForPlan = (moduleId, planId) => {
    return moduleService.isModuleAvailableForPlan(moduleId, planId);
  };

  const getAvailableModulesForPlan = (planId) => {
    return moduleService.getAvailableModulesForPlan(planId);
  };

  const clearMessages = () => {
    setError(null);
    setSuccessMessage(null);
  };

  const currentSiteData = sites.find(s => s.id === selectedSite);
  const currentPlan = sitePlans[selectedSite] || 'BASIC';
  const currentModules = siteModules[selectedSite] || {};
  const availableModules = getAvailableModulesForPlan(currentPlan);

  return (
    <div className="module-management">
      <div className="module-management-header">
        <h2>Gestión de Módulos</h2>
        <p>Habilita o deshabilita funcionalidades específicas para cada centro deportivo</p>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          <span className="alert-text">{error}</span>
          <button onClick={clearMessages} className="alert-close">×</button>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <span className="alert-icon">✅</span>
          <span className="alert-text">{successMessage}</span>
          <button onClick={clearMessages} className="alert-close">×</button>
        </div>
      )}

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <span>Cargando...</span>
        </div>
      )}

      {/* Site Selector */}
      <div className="site-selector">
        <label htmlFor="site-select">Seleccionar Centro Deportivo:</label>
        <select
          id="site-select"
          value={selectedSite || ''}
          onChange={(e) => setSelectedSite(e.target.value)}
          disabled={loading}
          className="site-select"
        >
          <option value="">Selecciona un centro deportivo</option>
          {sites.map(site => (
            <option key={site.id} value={site.id}>
              {site.name}
            </option>
          ))}
        </select>
      </div>

      {selectedSite && currentSiteData && (
        <div className="module-management-content">
          {/* Site Info */}
          <div className="site-info">
            <h3>{currentSiteData.name}</h3>
            <p>{currentSiteData.description}</p>
            <div className="site-plan-info">
              <span className="plan-badge plan-badge-{currentPlan.toLowerCase()}">
                Plan: {planDefinitions[currentPlan]?.name || currentPlan}
              </span>
            </div>
          </div>

          {/* Module Definitions */}
          <div className="module-definitions">
            <h4>Módulos Disponibles</h4>
            <div className="modules-grid">
              {Object.entries(moduleDefinitions).map(([moduleId, module]) => {
                const isAvailable = isModuleAvailableForPlan(moduleId, currentPlan);
                const isEnabled = currentModules[moduleId] === true;
                
                return (
                  <div key={moduleId} className={`module-card ${!isAvailable ? 'unavailable' : ''} ${isEnabled ? 'enabled' : 'disabled'}`}>
                    <div className="module-header">
                      <h5 className="module-name">{module.name}</h5>
                      <span className="module-plan-badge">
                        Plan: {module.plan}
                      </span>
                    </div>
                    
                    <p className="module-description">{module.description}</p>
                    
                    <div className="module-status">
                      {!isAvailable ? (
                        <span className="status-badge unavailable">
                          No disponible en este plan
                        </span>
                      ) : (
                        <div className="module-controls">
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={isEnabled}
                              onChange={(e) => handleModuleToggle(selectedSite, moduleId, e.target.checked)}
                              disabled={loading}
                            />
                            <span className="toggle-slider"></span>
                          </label>
                          <span className="status-text">
                            {isEnabled ? 'Habilitado' : 'Deshabilitado'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="quick-actions">
            <h4>Acciones Rápidas</h4>
            <div className="action-buttons">
              <button
                onClick={() => {
                  const updates = {};
                  availableModules.forEach(moduleId => {
                    updates[moduleId] = true;
                  });
                  handleBulkUpdate(selectedSite, updates);
                }}
                disabled={loading}
                className="btn btn-success"
              >
                Habilitar Todos los Disponibles
              </button>
              
              <button
                onClick={() => {
                  const updates = {};
                  availableModules.forEach(moduleId => {
                    updates[moduleId] = false;
                  });
                  handleBulkUpdate(selectedSite, updates);
                }}
                disabled={loading}
                className="btn btn-warning"
              >
                Deshabilitar Todos
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModuleManagement;
